<template>
  <div class="occupying-position-badge">
    <!-- Briefcase Icon (Primary) -->
    <div class="occupying-position-badge__icon-container">
      <img :src="briefcaseIcon" alt="Briefcase" class="occupying-position-badge__briefcase-icon" />
    </div>

    <!-- Employee Info or Not Assigned -->
    <div
      ref="statusRef"
      class="occupying-position-badge__status-container"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <template v-if="hasEmployee">
        <img
          v-if="employeeView.picture"
          :src="employeeView.picture"
          :alt="employeeView.name"
          class="occupying-position-badge__avatar"
        />
        <span
          v-else
          class="occupying-position-badge__avatar occupying-position-badge__avatar--initials"
          :aria-label="employeeView.name"
        >{{ employeeView.initials }}</span>
      </template>
      <template v-else>
        <div class="occupying-position-badge__not-assigned">
          <img :src="dotIcon" alt="Dot" class="occupying-position-badge__dot-icon" />
          <span class="occupying-position-badge__not-assigned-text">Not Assigned</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onBeforeUnmount } from 'vue';
import briefcaseIcon from '../assets/icons/briefcase.svg';
import dotIcon from '../assets/_Dot.svg';

const props = defineProps({
  employee: {
    type: [Object, Array],
    default: null
  }
});

const statusRef = ref(null);

const employeeView = computed(() => {
  const raw = Array.isArray(props.employee) ? props.employee[0] : props.employee;
  if (!raw) return { name: '', picture: '', initials: '' };

  // Resolve name — try all known field shapes from the position-detail API.
  // `primary_occupied_by[0]` uses first_name/last_name.
  // `occupied_by` (existing positions) uses assigned_employee_name.
  const first = raw.first_name || raw.firstName || '';
  const last = raw.last_name || raw.lastName || '';
  const fullFromParts = `${first} ${last}`.trim();
  const name =
    raw.name ||
    raw.full_name ||
    raw.employee_name ||
    raw.assigned_employee_name ||
    fullFromParts ||
    raw.email ||
    '';

  // Derive initials — split from whichever name source resolved.
  const nameParts = name.split(' ').filter(Boolean);
  const initFirst = first || nameParts[0] || '';
  const initLast = last || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : '');
  const initials = ((initFirst[0] || '') + (initLast[0] || '')).toUpperCase();

  return {
    name,
    picture:
      raw.picture ||
      raw.avatar ||
      raw.photo ||
      raw.photoUrl ||
      raw.assigned_employee_avatar ||
      '',
    initials: initials || (nameParts[0] ? nameParts[0][0].toUpperCase() : ''),
  };
});

// Consider the badge occupied whenever the prop is non-null — even if name
// and picture are both empty we still want to show the initials span rather
// than "Not Assigned" (ExAvatar renders "CW" as default in the same scenario).
const hasEmployee = computed(() => Boolean(props.employee));

const tooltipText = computed(() => {
  if (hasEmployee.value) {
    const name = (employeeView.value.name || '').trim();
    return name ? `Occupied by ${name}` : 'Occupied';
  }
  return 'None';
});

let tooltipEl = null;

const ensureTooltip = () => {
  if (tooltipEl) return tooltipEl;
  tooltipEl = document.createElement('div');
  tooltipEl.className = 'occupying-badge-cursor-tooltip';
  tooltipEl.innerHTML = '<span class="occupying-badge-cursor-tooltip__text"></span>';
  tooltipEl.style.display = 'none';
  document.body.appendChild(tooltipEl);
  return tooltipEl;
};

const onMouseEnter = () => {
  const target = statusRef.value;
  if (!target) return;
  // `getBoundingClientRect()` already returns viewport pixels — the same
  // coordinate space `position: fixed` uses — so no scale correction is
  // needed. The earlier `scaleX/scaleY` multipliers misplaced the tooltip
  // (off-screen) whenever any ancestor had a CSS transform applied.
  const rect = target.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const bottomY = rect.bottom + 8;

  const el = ensureTooltip();
  el.querySelector('.occupying-badge-cursor-tooltip__text').textContent = tooltipText.value;
  el.style.display = '';
  el.style.left = `${centerX}px`;
  el.style.top = `${bottomY}px`;
  el.style.transform = 'translateX(-50%)';
};

const onMouseLeave = () => {
  if (tooltipEl) tooltipEl.style.display = 'none';
};

onBeforeUnmount(() => {
  if (tooltipEl?.parentNode) {
    tooltipEl.parentNode.removeChild(tooltipEl);
    tooltipEl = null;
  }
});
</script>

<style scoped>
.occupying-position-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  box-sizing: border-box;
  justify-content: center;
  vertical-align: middle;
  line-height: 1;
}

.occupying-position-badge__icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.occupying-position-badge__briefcase-icon {
  width: 24px;
  height: 24px;
  display: block;
}

.occupying-position-badge__status-container {
  display: flex;
  align-items: center;
  cursor: default;
}

.occupying-position-badge__avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

/* Initials fallback — matches ExAvatar's visual (used elsewhere in the app)
   so an unphotographed employee still shows the same purple circle + initials. */
.occupying-position-badge__avatar--initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #F9F5FF;
  color: #7F56D9;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  user-select: none;
  line-height: 1;
}

.occupying-position-badge__not-assigned {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #F2F4F7;
  border-radius: 6px;
  border: 1px solid #EAECF0;
}

.occupying-position-badge__dot-icon {
  width: 8px;
  height: 8px;
  display: block;
}

.occupying-position-badge__not-assigned-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  user-select: none;
}
</style>

<style>
.occupying-badge-cursor-tooltip {
  position: fixed;
  z-index: 99999;
  background-color: #0C111D;
  border-radius: 8px;
  padding: 8px 12px;
  pointer-events: none;
  white-space: pre-line;
  box-shadow: 0px 4px 6px -2px rgba(16, 24, 40, 0.03),
              0px 12px 16px -4px rgba(16, 24, 40, 0.08);
}

.occupying-badge-cursor-tooltip__text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #ffffff;
}
</style>
